import { ASK_EXAMPLES } from './askExamples';
import { AskSearchError, requestAnswer } from './askSearch';
import {
  facetOptions,
  filterResults,
  sortResults,
  type FacetKey,
} from './facets';
import { isQuestionShaped, reciprocalRankFusion } from './fusion';
import {
  lexicalSearch,
  loadSearchIndex,
  queryTerms,
} from './lexicalSearch';
import { createRecentSearchStore } from './recentSearches';
import {
  requestSemanticResults,
  SemanticSearchError,
} from './semanticSearch';
import type {
  AskResponse,
  RankedSearchResult,
  SearchContentType,
  SearchFacetSelection,
  SearchIndex,
  SearchMode,
  SearchSort,
} from './searchTypes';
import {
  didYouMean,
  isWeakResults,
  relatedTopics,
} from './weakResults';

const CONTENT_FILTERS: { value: SearchContentType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'talk', label: 'Talk text' },
  { value: 'speaker', label: 'Speakers' },
  { value: 'theme', label: 'Themes' },
  { value: 'scripture', label: 'Scriptures' },
  { value: 'quote', label: 'Quotes' },
];

function appendHighlightedText(
  target: HTMLElement,
  value: string,
  terms: readonly string[],
): void {
  if (terms.length === 0) {
    target.textContent = value;
    return;
  }
  const pattern = new RegExp(
    `(${terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'giu',
  );
  for (const part of value.split(pattern)) {
    if (
      terms.some(
        (term) => term.toLocaleLowerCase() === part.toLocaleLowerCase(),
      )
    ) {
      const mark = document.createElement('mark');
      mark.textContent = part;
      target.append(mark);
    } else {
      target.append(document.createTextNode(part));
    }
  }
}

function snippet(text: string, terms: readonly string[]): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  const lower = normalized.toLocaleLowerCase();
  const firstMatch = terms
    .map((term) => lower.indexOf(term.toLocaleLowerCase()))
    .filter((position) => position >= 0)
    .sort((first, second) => first - second)
    .at(0);
  if (normalized.length <= 360) return normalized;
  const start = Math.max(0, (firstMatch ?? 0) - 100);
  const end = Math.min(normalized.length, start + 360);
  return `${start > 0 ? '…' : ''}${normalized.slice(start, end).trim()}${end < normalized.length ? '…' : ''}`;
}

function provenance(result: RankedSearchResult): string {
  if (result.sources.length === 2) return 'both';
  return result.sources.includes('semantic') ? 'meaning' : 'words';
}

function answerIsMiss(answer: string): boolean {
  return /(?:do not|don't|does not|doesn't|cannot|can't) (?:directly )?answer|not enough (?:information|context)|do not contain/iu.test(
    answer,
  );
}

function resultGroups(
  ranked: readonly RankedSearchResult[],
  query: string,
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const groups = new Map<string, RankedSearchResult[]>();
  for (const result of ranked) {
    const group = groups.get(result.document.talkId) ?? [];
    group.push(result);
    groups.set(result.document.talkId, group);
  }
  const terms = queryTerms(query);
  for (const matches of groups.values()) {
    const first = matches[0].document;
    const group = document.createElement('section');
    group.className = 'search-talk-group';
    group.dataset.talkId = first.talkId;
    const header = document.createElement('header');
    const title = document.createElement('h2');
    title.textContent = first.title;
    const meta = document.createElement('p');
    meta.textContent = `${first.speaker} · ${first.session}`;
    header.append(title, meta);
    const list = document.createElement('div');
    list.className = 'search-talk-group__matches';
    matches.forEach((result, index) => {
      const link = document.createElement('a');
      link.className = 'search-result';
      link.href = `/read/${result.document.talkId}/${result.document.paragraphId}`;
      link.dataset.router = '';
      link.dataset.paragraphId = result.document.paragraphId;
      link.dataset.matchSource = provenance(result);
      link.setAttribute(
        'aria-label',
        `Open ${result.document.title}, matching paragraph`,
      );
      if (index >= 3) link.hidden = true;
      const copy = document.createElement('p');
      copy.className = 'search-result__snippet';
      const source = provenance(result);
      const highlightedTerms = source === 'meaning' ? [] : terms;
      appendHighlightedText(
        copy,
        snippet(result.document.text, highlightedTerms),
        highlightedTerms,
      );
      const badge = document.createElement('span');
      badge.className = `search-result__badge search-result__badge--${source}`;
      badge.textContent = source;
      link.append(copy, badge);
      list.append(link);
    });
    group.append(header, list);
    if (matches.length > 3) {
      const showMore = document.createElement('button');
      showMore.type = 'button';
      showMore.className = 'search-talk-group__more';
      showMore.textContent = `Show ${String(matches.length - 3)} more matches`;
      showMore.addEventListener('click', () => {
        for (const match of list.querySelectorAll<HTMLElement>('[hidden]')) {
          match.hidden = false;
        }
        showMore.remove();
      });
      group.append(showMore);
    }
    fragment.append(group);
  }
  return fragment;
}

function initialState(mode: SearchMode): HTMLElement {
  const state = document.createElement('div');
  state.className = 'search-empty';
  const title = document.createElement('h2');
  title.textContent =
    mode === 'ask' ? 'Ask the conference' : 'Find a paragraph';
  const body = document.createElement('p');
  body.textContent =
    mode === 'ask'
      ? 'Ask a question and get a grounded answer with links to the source paragraphs.'
      : 'Search exact wording, a speaker, scripture, or conference theme.';
  state.append(title, body);
  return state;
}

function loadingState(): HTMLElement {
  const loading = document.createElement('div');
  loading.className = 'search-loading';
  loading.setAttribute('role', 'status');
  const title = document.createElement('h2');
  title.textContent = 'Reading talks…';
  const body = document.createElement('p');
  body.textContent = 'Finding and comparing the most relevant passages.';
  const skeleton = document.createElement('div');
  skeleton.className = 'search-loading__skeleton';
  skeleton.setAttribute('aria-hidden', 'true');
  loading.append(title, body, skeleton);
  return loading;
}

function searchLoadError(): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error page-width';
  state.dataset.route = 'search';
  state.setAttribute('role', 'alert');
  const title = document.createElement('h1');
  title.textContent = 'Search could not be loaded';
  const body = document.createElement('p');
  body.textContent = 'Refresh this page to load the conference search index.';
  state.append(title, body);
  return state;
}

function aiAccessNotice(kind: 'auth' | 'invite' | 'limit'): HTMLElement {
  const notice = document.createElement('section');
  notice.className = 'search-ai-access card';
  notice.setAttribute('role', kind === 'limit' ? 'alert' : 'status');
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'AI search';
  const heading = document.createElement('h2');
  heading.textContent =
    kind === 'auth'
      ? 'Sign in to use AI search'
      : kind === 'invite'
        ? 'AI search is invite-only'
        : 'Daily AI search limit reached';
  const body = document.createElement('p');
  body.textContent =
    kind === 'auth'
      ? 'Sign in to rank results by meaning and ask grounded questions about conference talks.'
      : kind === 'invite'
        ? 'AI search is currently available by invitation. Keyword search and all conference content remain available.'
        : 'You have used today’s AI searches. Keyword search and all conference content remain available; try AI search again tomorrow.';
  notice.append(eyebrow, heading, body);
  if (kind === 'auth') {
    const link = document.createElement('a');
    link.href = '/auth/google';
    link.className = 'button';
    link.textContent = 'Sign in with Google';
    notice.append(link);
  }
  return notice;
}

function createFacetControl(
  key: FacetKey,
  label: string,
): {
  root: HTMLDetailsElement;
  options: HTMLElement;
  summary: HTMLElement;
} {
  const root = document.createElement('details');
  root.className = 'search-facet';
  root.dataset.facet = key;
  const summary = document.createElement('summary');
  summary.textContent = label;
  const options = document.createElement('div');
  options.className = 'search-facet__options';
  root.append(summary, options);
  return { root, options, summary };
}

function answerCard(
  answer: AskResponse,
  index: SearchIndex,
): HTMLElement {
  const card = document.createElement('article');
  card.className = 'search-answer';
  card.setAttribute('aria-live', 'polite');
  card.setAttribute('aria-label', 'Conference answer');
  const label = document.createElement('p');
  label.className = 'eyebrow eyebrow--gold';
  label.textContent = 'Grounded answer';
  const heading = document.createElement('h2');
  heading.textContent = 'Answer';
  const body = document.createElement('p');
  body.className = 'search-answer__body';
  body.append(document.createTextNode(answer.answer));
  if (answer.citations.length > 0) {
    const citations = document.createElement('span');
    citations.className = 'search-answer__citations';
    answer.citations.forEach((citation, citationIndex) => {
      const documentMatch = index.documents.find(
        (document) => document.paragraphId === citation.paragraphId,
      );
      if (!documentMatch) return;
      const link = document.createElement('a');
      link.href = `/read/${documentMatch.talkId}/${citation.paragraphId}`;
      link.dataset.router = '';
      link.className = 'search-answer__citation';
      link.textContent = String(citationIndex + 1);
      link.title = `${documentMatch.title}: “${citation.quote}”`;
      link.setAttribute(
        'aria-label',
        `Citation ${String(citationIndex + 1)}: ${documentMatch.title}, ${documentMatch.speaker}`,
      );
      citations.append(link);
    });
    body.append(document.createTextNode(' '), citations);
  }
  const confidence = document.createElement('p');
  confidence.className = 'search-answer__confidence';
  confidence.textContent = `${answer.confidence} confidence`;
  card.append(label, heading, body, confidence);
  return card;
}

function answerResults(
  answer: AskResponse,
  index: SearchIndex,
): RankedSearchResult[] {
  const documents = new Map(
    index.documents.map((document) => [document.paragraphId, document]),
  );
  return answer.passages.flatMap((passage, resultIndex) => {
    const document = documents.get(passage.paragraphId);
    return document
      ? [
          {
            document,
            score: passage.score,
            semanticScore: passage.score,
            rank: resultIndex + 1,
            sources: ['semantic'] as const,
            matchedTypes: [],
          },
        ]
      : [];
  });
}

export async function createSearchPage(conferenceId: string): Promise<HTMLElement> {
  let index: SearchIndex;
  try {
    index = await loadSearchIndex(conferenceId);
  } catch {
    return searchLoadError();
  }

  const parameters = new URLSearchParams(window.location.search);
  const talkCount = new Set(index.documents.map((document) => document.talkId)).size;
  let mode: SearchMode = parameters.get('mode') === 'ask' ? 'ask' : 'find';
  let baseResults: RankedSearchResult[] = [];
  let activeAnswer: AskResponse | undefined;
  let activeController: AbortController | undefined;
  let activeQueryVersion = 0;
  let selectedContentType: SearchContentType = 'all';
  let selectedSort: SearchSort = 'relevance';
  const selection: SearchFacetSelection = {
    speakers: new Set(),
    sessions: new Set(),
    topics: new Set(),
  };

  const root = document.createElement('div');
  root.className = 'search-page page-width';
  root.dataset.route = 'search';

  const hero = document.createElement('header');
  hero.className = 'search-hero';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Conference search';
  const heading = document.createElement('h1');
  const intro = document.createElement('p');
  hero.append(eyebrow, heading, intro);

  const tabs = document.createElement('div');
  tabs.className = 'search-tabs';
  tabs.setAttribute('role', 'tablist');
  tabs.setAttribute('aria-label', 'Search mode');
  const findTab = document.createElement('button');
  findTab.type = 'button';
  findTab.id = 'search-tab-find';
  findTab.dataset.mode = 'find';
  findTab.setAttribute('role', 'tab');
  findTab.textContent = 'Find';
  const askTab = document.createElement('button');
  askTab.type = 'button';
  askTab.id = 'search-tab-ask';
  askTab.dataset.mode = 'ask';
  askTab.setAttribute('role', 'tab');
  askTab.textContent = 'Ask';
  tabs.append(findTab, askTab);

  const form = document.createElement('form');
  form.className = 'search-form';
  form.setAttribute('role', 'search');
  const label = document.createElement('label');
  label.htmlFor = 'conference-search-query';
  const inputRow = document.createElement('div');
  inputRow.className = 'search-form__row';
  const input = document.createElement('input');
  input.id = 'conference-search-query';
  input.name = 'q';
  input.type = 'search';
  input.autocomplete = 'off';
  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'button button--gold';
  inputRow.append(input, submit);
  form.append(label, inputRow);

  const examples = document.createElement('div');
  examples.className = 'search-suggestions';
  const examplesLabel = document.createElement('span');
  examplesLabel.textContent = 'Try:';
  examples.append(examplesLabel);
  for (const example of ASK_EXAMPLES) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = example;
    button.addEventListener('click', () => {
      input.value = example;
      form.requestSubmit();
    });
    examples.append(button);
  }

  const filterBar = document.createElement('section');
  filterBar.className = 'search-filter-bar';
  filterBar.setAttribute('aria-label', 'Filter search results');
  filterBar.hidden = true;
  const facetRow = document.createElement('div');
  facetRow.className = 'search-filter-bar__row';
  const speakerFacet = createFacetControl('speaker', 'Speaker');
  const sessionFacet = createFacetControl('session', 'Session');
  const topicFacet = createFacetControl('topic', 'Topic');
  const sortLabel = document.createElement('label');
  sortLabel.className = 'search-sort';
  sortLabel.textContent = 'Sort';
  const sort = document.createElement('select');
  const relevanceOption = new Option('Relevance', 'relevance');
  const conferenceOption = new Option('Conference order', 'conference');
  sort.append(relevanceOption, conferenceOption);
  sortLabel.append(sort);
  facetRow.append(
    speakerFacet.root,
    sessionFacet.root,
    topicFacet.root,
    sortLabel,
  );
  const activeFilters = document.createElement('div');
  activeFilters.className = 'search-active-filters';
  activeFilters.setAttribute('aria-label', 'Active filters');
  filterBar.append(facetRow, activeFilters);

  const contentFilters = document.createElement('fieldset');
  contentFilters.className = 'search-filters';
  const contentLegend = document.createElement('legend');
  contentLegend.textContent = 'Match type';
  contentFilters.append(contentLegend);
  for (const contentFilter of CONTENT_FILTERS) {
    const contentLabel = document.createElement('label');
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'search-type';
    radio.value = contentFilter.value;
    radio.checked = contentFilter.value === 'all';
    const text = document.createElement('span');
    text.textContent = contentFilter.label;
    contentLabel.append(radio, text);
    contentFilters.append(contentLabel);
  }

  const status = document.createElement('p');
  status.className = 'search-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const aiNoticeRegion = document.createElement('div');
  aiNoticeRegion.className = 'search-ai-notice-region';
  const answerRegion = document.createElement('section');
  answerRegion.className = 'search-answer-region';
  const results = document.createElement('section');
  results.className = 'search-results';
  results.setAttribute('aria-label', 'Search results');

  const recentStore = createRecentSearchStore();
  const recent = document.createElement('section');
  recent.className = 'recent-searches';
  const recentTitle = document.createElement('h2');
  recentTitle.textContent = 'Recent searches';
  const recentList = document.createElement('div');
  recent.append(recentTitle, recentList);
  const renderRecent = (items: readonly string[]): void => {
    recent.hidden = items.length === 0;
    recentList.replaceChildren();
    for (const item of items) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = item;
      button.addEventListener('click', () => {
        input.value = item;
        form.requestSubmit();
      });
      recentList.append(button);
    }
  };
  renderRecent(recentStore.load());

  const updateUrl = (historyMode: 'push' | 'replace' = 'replace'): void => {
    const url = new URL(window.location.href);
    const query = input.value.trim();
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    if (mode === 'ask') url.searchParams.set('mode', 'ask');
    else url.searchParams.delete('mode');
    const href = `${url.pathname}${url.search}`;
    if (historyMode === 'push') window.history.pushState({}, '', href);
    else window.history.replaceState({}, '', href);
  };

  const runSuggestion = (query: string, targetMode = mode): void => {
    input.value = query;
    if (targetMode !== mode) setMode(targetMode, false, true);
    form.requestSubmit();
  };

  const createAskHandoff = (query: string): HTMLElement => {
    const handoff = document.createElement('p');
    handoff.className = 'search-ask-handoff';
    handoff.append(document.createTextNode('Looking for an answer? '));
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Try Ask';
    button.addEventListener('click', () => runSuggestion(query, 'ask'));
    handoff.append(button);
    return handoff;
  };

  const weakModule = (
    query: string,
    nearest: readonly RankedSearchResult[],
    includeHandoff: boolean,
  ): HTMLElement => {
    const weak = document.createElement('aside');
    weak.className = 'search-weak';
    const title = document.createElement('h2');
    title.textContent =
      nearest.length === 0 ? 'No close matches yet' : 'These matches may be broad';
    const hint = document.createElement('p');
    hint.textContent =
      'Try fewer words, a synonym, a speaker name, or one central idea.';
    weak.append(title, hint);
    const topics = relatedTopics(nearest);
    if (topics.length > 0) {
      const topicLabel = document.createElement('p');
      topicLabel.textContent = 'Closest themes:';
      const topicList = document.createElement('div');
      topicList.className = 'search-weak__chips';
      for (const topic of topics) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = topic;
        button.addEventListener('click', () => runSuggestion(topic, 'find'));
        topicList.append(button);
      }
      weak.append(topicLabel, topicList);
    }
    const suggestions = didYouMean(query, index.documents);
    if (suggestions.length > 0) {
      const suggestionLabel = document.createElement('p');
      suggestionLabel.textContent = 'Did you mean:';
      const suggestionList = document.createElement('div');
      suggestionList.className = 'search-weak__chips';
      for (const suggestion of suggestions) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = suggestion;
        button.addEventListener('click', () =>
          runSuggestion(suggestion, 'find'),
        );
        suggestionList.append(button);
      }
      weak.append(suggestionLabel, suggestionList);
    }
    if (includeHandoff) weak.append(createAskHandoff(query));
    return weak;
  };

  const renderFacet = (
    control: ReturnType<typeof createFacetControl>,
    key: FacetKey,
    selected: Set<string>,
    source: readonly RankedSearchResult[],
  ): void => {
    control.options.replaceChildren();
    const options = facetOptions(source, key);
    control.root.hidden = options.length === 0;
    control.summary.textContent =
      selected.size > 0
        ? `${key[0].toUpperCase()}${key.slice(1)} (${String(selected.size)})`
        : `${key[0].toUpperCase()}${key.slice(1)}`;
    for (const option of options) {
      const labelElement = document.createElement('label');
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.value = option.value;
      checkbox.checked = selected.has(option.value);
      const text = document.createElement('span');
      text.textContent = `${option.value} (${String(option.count)})`;
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) selected.add(option.value);
        else selected.delete(option.value);
        control.root.open = false;
        renderCurrentResults();
      });
      labelElement.append(checkbox, text);
      control.options.append(labelElement);
    }
  };

  const renderActiveFilters = (): void => {
    activeFilters.replaceChildren();
    const selectedEntries: [Set<string>, string][] = [
      [selection.speakers, 'Speaker'],
      [selection.sessions, 'Session'],
      [selection.topics, 'Topic'],
    ];
    for (const [values, labelPrefix] of selectedEntries) {
      for (const value of values) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'search-filter-chip';
        chip.textContent = `${labelPrefix}: ${value} ×`;
        chip.setAttribute('aria-label', `Remove ${labelPrefix} filter ${value}`);
        chip.addEventListener('click', () => {
          values.delete(value);
          renderCurrentResults();
        });
        activeFilters.append(chip);
      }
    }
    if (activeFilters.childElementCount > 0) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'search-filter-clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', () => {
        selection.speakers.clear();
        selection.sessions.clear();
        selection.topics.clear();
        renderCurrentResults();
      });
      activeFilters.append(clear);
    }
  };

  const filteredBaseResults = (): RankedSearchResult[] => {
    const activeType = selectedContentType;
    return mode === 'find' && activeType !== 'all'
      ? baseResults.filter((result) =>
          result.matchedTypes.includes(activeType),
        )
      : baseResults;
  };

  const renderCurrentResults = (): void => {
    const query = input.value.trim();
    results.replaceChildren();
    if (!query) {
      filterBar.hidden = true;
      results.append(initialState(mode));
      return;
    }
    const typedResults = filteredBaseResults();
    renderFacet(speakerFacet, 'speaker', selection.speakers, typedResults);
    renderFacet(sessionFacet, 'session', selection.sessions, typedResults);
    renderFacet(topicFacet, 'topic', selection.topics, typedResults);
    renderActiveFilters();
    filterBar.hidden = baseResults.length === 0;
    sortLabel.hidden = mode === 'ask';
    contentFilters.hidden = mode === 'ask';
    const filtered = filterResults(typedResults, selection);
    const ranked = sortResults(filtered, selectedSort, index.documents);
    const questionHandoff =
      mode === 'find' && isQuestionShaped(query);
    if (questionHandoff) results.append(createAskHandoff(query));
    if (
      ranked.length === 0 ||
      isWeakResults(ranked) ||
      (mode === 'ask' &&
        (activeAnswer?.confidence === 'low' ||
          answerIsMiss(activeAnswer?.answer ?? '')))
    ) {
      results.append(
        weakModule(query, typedResults.slice(0, 12), false),
      );
    }
    if (ranked.length > 0) {
      if (mode === 'ask') {
        const passagesTitle = document.createElement('h2');
        passagesTitle.className = 'search-results__title';
        passagesTitle.textContent = 'Passages used';
        results.append(passagesTitle);
      }
      results.append(resultGroups(ranked, query));
    }
  };

  const renderAskError = (message: string, query: string): void => {
    answerRegion.replaceChildren();
    results.replaceChildren();
    filterBar.hidden = true;
    const error = document.createElement('div');
    error.className = 'search-ask-error';
    error.setAttribute('role', 'alert');
    const title = document.createElement('h2');
    title.textContent = 'An answer could not be created';
    const body = document.createElement('p');
    body.textContent = message;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => runAsk(query, false));
    error.append(title, body, retry);
    results.append(error);
  };

  const runFind = (query: string, updateHistory: boolean): void => {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    const queryVersion = ++activeQueryVersion;
    activeAnswer = undefined;
    answerRegion.replaceChildren();
    aiNoticeRegion.replaceChildren();
    const lexical = lexicalSearch(index, query, 'all');
    baseResults = lexical;
    renderCurrentResults();
    status.textContent = `${String(lexical.length)} word matches. Improving result order with meaning.`;
    if (updateHistory) renderRecent(recentStore.add(query));
    void requestSemanticResults(query, conferenceId, controller.signal)
      .then((semantic) => {
        if (queryVersion !== activeQueryVersion) return;
        baseResults = reciprocalRankFusion(
          lexical,
          semantic,
          index.documents,
          50,
          query,
        );
        renderCurrentResults();
        status.textContent = `${String(filteredBaseResults().length)} results ranked by wording and meaning.`;
      })
      .catch((error: unknown) => {
        if (queryVersion !== activeQueryVersion || controller.signal.aborted) {
          return;
        }
        if (error instanceof SemanticSearchError) {
          if (error.code === 'AUTH_REQUIRED') {
            aiNoticeRegion.replaceChildren(aiAccessNotice('auth'));
            status.textContent = `${String(lexical.length)} word-only results. Sign in to rank by meaning.`;
            return;
          } else if (error.code === 'AI_ACCESS_DENIED') {
            aiNoticeRegion.replaceChildren(aiAccessNotice('invite'));
            status.textContent = `${String(lexical.length)} word-only results. AI search is invite-only.`;
            return;
          } else if (error.code === 'AI_SEARCH_DAILY_LIMIT_REACHED') {
            aiNoticeRegion.replaceChildren(aiAccessNotice('limit'));
            status.textContent = `${String(lexical.length)} word-only results. Daily AI search limit reached.`;
            return;
          }
        }
        status.textContent = `${String(lexical.length)} word-only results. Meaning search is temporarily unavailable.`;
      });
  };

  const runAsk = (query: string, updateHistory: boolean): void => {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    const queryVersion = ++activeQueryVersion;
    activeAnswer = undefined;
    baseResults = [];
    filterBar.hidden = true;
    aiNoticeRegion.replaceChildren();
    answerRegion.replaceChildren(loadingState());
    results.replaceChildren();
    status.textContent = 'Reading talks and preparing a grounded answer.';
    if (updateHistory) renderRecent(recentStore.add(query));
    void requestAnswer(query, conferenceId, controller.signal)
      .then((answer) => {
        if (queryVersion !== activeQueryVersion) return;
        activeAnswer = answer;
        baseResults = answerResults(answer, index);
        answerRegion.replaceChildren(answerCard(answer, index));
        renderCurrentResults();
        status.textContent = `Answer ready with ${String(answer.citations.length)} citations and ${String(baseResults.length)} source passages.`;
      })
      .catch((error: unknown) => {
        if (queryVersion !== activeQueryVersion || controller.signal.aborted) {
          return;
        }
        answerRegion.replaceChildren();
        if (error instanceof AskSearchError) {
          if (error.code === 'AUTH_REQUIRED') {
            results.replaceChildren(aiAccessNotice('auth'));
            status.textContent = '';
            return;
          }
          if (error.code === 'AI_ACCESS_DENIED') {
            results.replaceChildren(aiAccessNotice('invite'));
            status.textContent = '';
            return;
          }
          if (error.code === 'AI_SEARCH_DAILY_LIMIT_REACHED') {
            results.replaceChildren(aiAccessNotice('limit'));
            status.textContent = '';
            return;
          }
        }
        const message =
          error instanceof Error
            ? error.message
            : 'Question answering is temporarily unavailable.';
        status.textContent = '';
        renderAskError(message, query);
      });
  };

  const runSearch = (query: string, updateHistory = true): void => {
    const trimmed = query.trim();
    if (!trimmed) {
      activeController?.abort();
      baseResults = [];
      activeAnswer = undefined;
      answerRegion.replaceChildren();
      status.textContent = '';
      renderCurrentResults();
      return;
    }
    if (mode === 'ask') runAsk(trimmed, updateHistory);
    else runFind(trimmed, updateHistory);
  };

  function setMode(
    nextMode: SearchMode,
    run = true,
    addHistory = true,
  ): void {
    if (nextMode === mode && run) return;
    const changed = nextMode !== mode;
    mode = nextMode;
    root.dataset.mode = mode;
    for (const tab of [findTab, askTab]) {
      const selected = tab.dataset.mode === mode;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    examples.hidden = mode !== 'ask';
    contentFilters.hidden = mode === 'ask';
    sortLabel.hidden = mode === 'ask';
    heading.textContent = mode === 'ask' ? 'Ask a question' : 'Find a paragraph';
    intro.textContent =
      mode === 'ask'
        ? 'Get a concise answer grounded in the conference talks, with citations you can open and read.'
        : `Search wording, names, scriptures, and themes across all ${String(talkCount)} talks.`;
    label.textContent =
      mode === 'ask' ? 'Ask the conference' : 'Search the conference';
    input.placeholder =
      mode === 'ask'
        ? 'How can I find peace during trials?'
        : 'Try a phrase, scripture, speaker, or theme';
    submit.textContent = mode === 'ask' ? 'Ask' : 'Search';
    updateUrl(changed && addHistory ? 'push' : 'replace');
    if (run) runSearch(input.value, false);
  }

  findTab.addEventListener('click', () => setMode('find'));
  askTab.addEventListener('click', () => setMode('ask'));
  tabs.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const nextMode = mode === 'find' ? 'ask' : 'find';
    setMode(nextMode);
    (nextMode === 'find' ? findTab : askTab).focus();
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    updateUrl();
    runSearch(input.value);
  });
  contentFilters.addEventListener('change', () => {
    selectedContentType =
      (contentFilters.querySelector<HTMLInputElement>(
        'input[name="search-type"]:checked',
      )?.value as SearchContentType | undefined) ?? 'all';
    renderCurrentResults();
  });
  sort.addEventListener('change', () => {
    selectedSort = sort.value as SearchSort;
    renderCurrentResults();
  });
  window.addEventListener('popstate', () => {
    const urlState = new URLSearchParams(window.location.search);
    input.value = urlState.get('q') ?? '';
    setMode(urlState.get('mode') === 'ask' ? 'ask' : 'find', true, false);
  });

  input.value = parameters.get('q') ?? '';
  root.append(
    hero,
    tabs,
    form,
    examples,
    contentFilters,
    filterBar,
    status,
    aiNoticeRegion,
    recent,
    answerRegion,
    results,
  );
  setMode(mode, false, false);
  if (input.value) runSearch(input.value);
  else renderCurrentResults();
  return root;
}
